import React, { useState, useEffect } from 'react';
import { X, Users, Check, Info } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import MappingAutocomplete from '../common/MappingAutocomplete';
import { KONTIERUNGSTYPEN, isKontierungFieldVisible } from '../../core/utils/kontierung';

/**
 * Popup zum Übernehmen einer Kontierung auf ALLE Warenkorb-Positionen.
 * Werte werden aus der ersten kontierten Position vorbefüllt und können
 * vor dem Übernehmen manuell angepasst werden.
 *
 * Schreibt die kanonischen Spalten cost_center + gl_account (+ plant, kontierungstyp);
 * leere Felder werden NICHT übertragen (überschreiben also keine gültigen Werte).
 *
 * Props:
 *   user  { token, ... }  — für MappingAutocomplete-Requests nötig
 */
const KontierungBulkModal = ({ isOpen, onClose, onApply, initial = {}, itemCount = 0, user }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [form, setForm] = useState({ kontierungstyp: 'kostenstelle', auftrag: '', cost_center: '', gl_account: '', plant: '' });
  const [submitting, setSubmitting] = useState(false);

  // Beim Öffnen aus der Quell-Position vorbefüllen
  useEffect(() => {
    if (isOpen) {
      setForm({
        kontierungstyp: initial.kontierungstyp || 'kostenstelle',
        auftrag: initial.auftrag || '',
        cost_center: initial.cost_center || initial.kostenstelle || '',
        gl_account: initial.gl_account || initial.sachkonto || '',
        plant: initial.plant || ''
      });
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (k) => (val) => setForm((f) => ({ ...f, [k]: val }));

  const handleApply = async () => {
    setSubmitting(true);
    // Nur gesetzte Felder übertragen
    const payload = {};
    if (form.kontierungstyp) payload.kontierungstyp = form.kontierungstyp;
    if (form.auftrag.trim()) payload.auftrag = form.auftrag.trim();
    if (form.cost_center.trim()) payload.cost_center = form.cost_center.trim();
    if (form.gl_account.trim()) payload.gl_account = form.gl_account.trim();
    if (form.plant.trim()) payload.plant = form.plant.trim();
    await onApply(payload);
  };

  const labelCls = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`;
  const selectCls = `w-full rounded-xl px-3 py-2 text-sm border focus:ring-2 focus:ring-brand/40 focus:outline-none transition-colors ${
    isDarkMode
      ? 'bg-gray-800/40 text-white border-white/10 placeholder-white/30'
      : 'bg-white text-slate-800 border-slate-200 placeholder-slate-300'
  }`;

  const token = user?.token;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 ${isDarkMode ? 'bg-black/60 backdrop-blur-sm' : 'bg-slate-900/30 backdrop-blur-sm'}`}
        onClick={onClose}
      />

      {/* Dialog */}
      <div className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
        isDarkMode ? 'bg-gray-900 border-white/10' : 'bg-white border-slate-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
              <Users className="w-5 h-5 text-brand" />
            </div>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('cart.kontModalTitle')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-slate-100 text-slate-400'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div className={`flex items-start gap-2 text-xs rounded-xl px-3 py-2 ${
            isDarkMode ? 'bg-blue-900/20 text-blue-200/80 border border-blue-400/20' : 'bg-brand/5 text-slate-600 border border-brand/15'
          }`}>
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-brand" />
            <span>{t('cart.kontModalDesc').replace('{n}', String(itemCount))}</span>
          </div>

          {/* Kontierungstyp — steuert dynamisch die sichtbaren Felder (zentrale Logik) */}
          <div>
            <label className={labelCls}>{t('cart.kontType')}</label>
            <select
              value={form.kontierungstyp}
              onChange={(e) => setForm((f) => ({ ...f, kontierungstyp: e.target.value }))}
              className={selectCls}
            >
              {KONTIERUNGSTYPEN.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Auftrag — nur Kontierungstyp 'auftrag' */}
          {isKontierungFieldVisible(form.kontierungstyp, 'auftrag') && (
            <div>
              <label className={labelCls}>{t('missingFields.auftrag')}</label>
              <input
                type="text"
                value={form.auftrag}
                onChange={(e) => set('auftrag')(e.target.value)}
                placeholder={t('missingFields.auftragPlaceholder')}
                className={selectCls}
                autoComplete="off"
              />
            </div>
          )}

          {/* Kostenstelle + Sachkonto — je nach Kontierungstyp */}
          {(isKontierungFieldVisible(form.kontierungstyp, 'cost_center') || isKontierungFieldVisible(form.kontierungstyp, 'gl_account')) && (
            <div className={isKontierungFieldVisible(form.kontierungstyp, 'cost_center') && isKontierungFieldVisible(form.kontierungstyp, 'gl_account') ? 'grid grid-cols-2 gap-3' : ''}>
              {isKontierungFieldVisible(form.kontierungstyp, 'cost_center') && (
                <div>
                  <label className={labelCls}>{t('cart.kontCostCenter')}</label>
                  <MappingAutocomplete
                    type="cost_center"
                    value={form.cost_center}
                    onChange={set('cost_center')}
                    placeholder={t('cart.kontCostCenterPlaceholder')}
                    isDarkMode={isDarkMode}
                    token={token}
                  />
                </div>
              )}
              {isKontierungFieldVisible(form.kontierungstyp, 'gl_account') && (
                <div>
                  <label className={labelCls}>{t('cart.kontGlAccount')}</label>
                  <MappingAutocomplete
                    type="gl_account"
                    value={form.gl_account}
                    onChange={set('gl_account')}
                    placeholder={t('cart.kontGlAccountPlaceholder')}
                    isDarkMode={isDarkMode}
                    token={token}
                  />
                </div>
              )}
            </div>
          )}

          {/* Werk — typ-unabhängig */}
          <div>
            <label className={labelCls}>{t('cart.kontPlant')}</label>
            <MappingAutocomplete
              type="plant"
              value={form.plant}
              onChange={set('plant')}
              placeholder={t('cart.kontPlantPlaceholder')}
              isDarkMode={isDarkMode}
              token={token}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isDarkMode ? 'text-white/70 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t('cart.kontCancel')}
          </button>
          <button
            onClick={handleApply}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-brand hover:bg-[#1D4ED8] disabled:opacity-60 transition-colors"
          >
            <Check className="w-4 h-4" />
            {t('cart.kontApply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KontierungBulkModal;
